import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  getFields,
  saveStep,
  updateStep,
  uploadFile,
  getTotalDependents,
} from "../../../store/policy";
import Upload from "../../common/Upload";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import styles from "./index.module.css";
import {
  User,
  Mail,
  Phone,
  Home,
  Globe,
  Calendar,
  Upload as UploadIcon,
  IdCard,
  ArrowRight,
  Loader,
} from "lucide-react";
import { validationRules } from "../../common/Utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  checkEmailExists,
  resetEmailCacheForStep,
  setCurrentStep,
} from "../../../store/medicalAssessmentSlice";
dayjs.extend(customParseFormat);

const { Option } = Select;

function EnhancedStep1({ current, setCurrent }) {
  const [form1] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fileList, setFileList] = useState([]);
  const [sortedFields, setSortedFields] = useState([]);
  const existingValues = sessionStorage.getItem("values");
  const coverageCountry = localStorage.getItem("coverageCountry")
    ? localStorage.getItem("coverageCountry")
    : "";

  const { steps, fields, fileUploading } = useSelector(
    (state) => state.policySlice,
  );
  const { emailCheckStatus } = useSelector((state) => state.medicalAssessment);
  let initial = true;

  const stepId = steps?.find((s) => s?.step_number === current)?.id;

  useEffect(() => {
    if (stepId) {
      dispatch(setCurrentStep(stepId));
      dispatch(resetEmailCacheForStep(stepId));
    }
  }, [stepId, dispatch]);

  const [memberInfo, setMemberInfo] = useState(null);

  useEffect(() => {
    dispatch(getTotalDependents())
      .then((response) => {
        if (response.payload?.success) {
          setMemberInfo(response.payload.result);
          const primaryMember = response.payload.result.primary_member;
          if (primaryMember) {
            form1.setFieldsValue({
              first_name: primaryMember?.first_name,
              last_name: primaryMember?.last_name,
              email: primaryMember?.email,
              phone_number: primaryMember?.phone_number,
              dob: moment(
                primaryMember?.principal_dob ?? primaryMember?.dob,
                "YYYY-MM-DD",
              ),
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching member info:", error);
      });
  }, [dispatch]);

  const onFinish = async (values) => {
    const formData = new Object();
    Object.keys(values).forEach((key) => {
      if (key === "upload_id") {
        formData[key] = fileList;
      } else if (key === "dob") {
        formData[key] = moment(new Date(values[key])).format("YYYY-MM-DD");
      } else {
        formData[key] = values[key];
      }
    });

    const mappedArray = Object.keys(formData)?.map((key, i) => {
      const field = fields.find((field) => field.field_name === key);
      if (field) {
        return {
          field_id: field.id,
          key: field.field_name,
          value: formData[key],
        };
      }
    });

    const emailFields = Object.entries(formData).filter(([key, value]) => {
      return (key.includes("email") || key.includes("mail")) && value;
    });

    for (const [fieldName, value] of emailFields) {
      const result = await dispatch(
        checkEmailExists({
          email: value,
          stepId: stepId,
        }),
      );

      if (result.payload?.exists) {
        return;
      }
    }

    if (
      !form1.isFieldsTouched() &&
      existingValues &&
      JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id,
      )
    ) {
      setCurrent(2);
    } else if (
      existingValues &&
      JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id,
      )
    ) {
      dispatch(
        updateStep({
          userId: JSON.parse(sessionStorage.getItem("user"))?.userId,
          stepId: steps.find((s) => s?.step_number === current)?.id,
          parameters: mappedArray,
        }),
      ).then(() => {
        localStorage.removeItem("lastStep");
        setCurrent(2);
      });
    } else {
      dispatch(
        saveStep({
          source: steps.find((s) => s?.step_number === current)?.step_source,
          user_id: JSON.parse(sessionStorage.getItem("user"))?.userId,
          step_id: steps.find((s) => s?.step_number === current)?.id,
          parameters: mappedArray,
        }),
      ).then(() => setCurrent(2));
    }
  };

  useEffect(() => {
    if (
      steps &&
      steps.find((s) => s?.step_number === current) &&
      steps.find((s) => s?.step_number === current).form_id &&
      steps.find((s) => s?.step_number === current).id
    ) {
      dispatch(
        getFields({
          formId: steps.find((s) => s?.step_number === current).form_id,
          stepId: steps.find((s) => s?.step_number === current).id,
        }),
      ).then((res) => {
        const localFields = res?.payload &&
          Array.isArray(res?.payload) && [...res?.payload];
        setSortedFields(
          localFields?.sort((a, b) => a?.field_sequence - b?.field_sequence),
        );
      });
    }
  }, [steps]);

  useEffect(() => {
    if (
      existingValues &&
      JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id === steps?.find((s) => s?.step_number === current)?.id,
      ) &&
      initial
    ) {
      initial = false;
      const values = JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id === steps?.find((s) => s?.step_number === current)?.id,
      ).parameters;

      const obj = new Object();
      for (let key of values) {
        if (key.key === "dob") {
          obj[key.key] = moment(key.value, "YYYY-MM-DD");
        } else if (key.key === "upload_id" && key.value) {
          obj[key.key] = key.value;
          setFileList(key.value);
        } else {
          obj[key.key] = key.value;
        }
      }
      form1.setFieldsValue(obj);
    }
  }, [steps]);

  const handleChange = async ({ file }) => {
    if (file.status === "removed") {
      setFileList();
      return;
    } else {
      const formData = new FormData();
      formData.append("file", file);
      dispatch(uploadFile(formData))
        .then((res) => {
          form1.setFieldsValue({
            signature_date: moment(),
            signature_upload: res?.payload?.result,
          });
          setFileList({
            ...res?.payload?.result,
            name: res?.payload?.result?.file_name,
          });
        })
        .catch((err) => console.log(err));
    }
  };

  const getFieldIcon = (fieldType, fieldName) => {
    if (fieldName?.toLowerCase().includes("email")) {
      return <Mail size={16} color="#028D86" />;
    } else if (fieldName?.toLowerCase().includes("name")) {
      return <User size={16} color="#028D86" />;
    } else if (fieldName?.toLowerCase().includes("phone")) {
      return <Phone size={16} color="#028D86" />;
    } else if (fieldName?.toLowerCase().includes("address")) {
      return <Home size={16} color="#028D86" />;
    } else if (fieldName?.toLowerCase().includes("country")) {
      return <Globe size={16} color="#028D86" />;
    } else if (
      fieldType === "date" ||
      fieldName?.toLowerCase().includes("dob")
    ) {
      return <Calendar size={16} color="#028D86" />;
    } else if (fieldType === "file") {
      return <UploadIcon size={16} color="#028D86" />;
    } else {
      return <IdCard size={16} color="#028D86" />;
    }
  };

  return (
    <div className={styles.stepWrapper}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <User size={22} />
          </div>
          <div>
            <h3 className={styles.cardTitle}>Principal Member</h3>
            <span className={styles.cardDesc}>
              Provide more details about the Principal Member
            </span>
          </div>
        </div>

        <div className={styles.cardBody}>
          <Form
            form={form1}
            layout="vertical"
            onFinish={onFinish}
          >
            <div className={styles.formGrid}>
              {sortedFields &&
                sortedFields.length > 0 &&
                sortedFields?.map((field, index) => {
                  return (
                    <Form.Item
                      className={`${styles.formItem} ${field?.type === "file" ? styles.fileUpload : ""}`}
                      key={index}
                      label={
                        <span className={styles.fieldLabel}>
                          {getFieldIcon(field?.type, field?.field_name)}
                          {field?.label}
                        </span>
                      }
                      id={field?.id}
                      name={field?.field_name}
                      rules={validationRules(
                        field?.validation,
                        field?.label,
                        field?.type,
                      )}
                      tooltip={field?.description}
                    >
                      {field?.type === "text" || field?.type === "number" ? (
                        <Input
                          allowClear
                          className={styles.fieldInput}
                          value={field?.value}
                          placeholder={`Enter ${field?.label.toLowerCase()}`}
                          disabled={field?.field_name === "email"}
                        />
                      ) : field?.type === "file" ? (
                        <div>
                          <Upload
                            showUploadList={{ showRemoveIcon: false }}
                            fileList={fileList ? [fileList] : []}
                            beforeUpload={() => false}
                            onChange={handleChange}
                            onRemove={() => setFileList([])}
                            width="100%"
                          />
                          {fileUploading ? <Loader size={16} className={styles.spinner} /> : ""}
                        </div>
                      ) : field?.type === "date" ? (
                        <DatePicker
                          allowClear
                          className={styles.fieldDatePicker}
                          format={"YYYY-MM-DD"}
                          maxDate={dayjs().subtract(18, "year")}
                          disabledDate={(current) => {
                            return (
                              current &&
                              (current > moment().endOf("day") ||
                                current >
                                  moment().subtract(18, "years").endOf("day"))
                            );
                          }}
                        />
                      ) : field?.type === "dropdown" ? (
                        <Select
                          allowClear
                          placeholder={`Select ${field?.label.toLowerCase()}`}
                          options={field?.dropdowns
                            ?.filter((r) =>
                              r?.regions?.find((r) => r === coverageCountry),
                            )?.[0]
                            ?.values?.map((opt) => {
                              return { label: opt?.name, value: opt?.value };
                            })}
                          className={styles.fieldSelect}
                          value={field?.value}
                          showSearch
                          optionFilterProp="label"
                        />
                      ) : (
                        ""
                      )}
                    </Form.Item>
                  );
                })}
            </div>

            <div className={styles.actions}>
              <Button
                htmlType="submit"
                type="primary"
                className={styles.continueBtn}
                loading={emailCheckStatus === "loading"}
              >
                Continue
                <ArrowRight size={16} />
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default EnhancedStep1;
